import { useState, useEffect } from "react";
import type { FieldContent } from "@capture/shared";
import { api } from "@/lib/api-client";
import { useFormConfigStore } from "../form-config/state/store";

export interface FieldDefinitionSummary {
  id: string;
  name: string;
  label: string;
  dataType: string;
  groupId: string | null;
  groupName: string;
}

/** F-056 — ONE form's field bindings as picker summaries. The automations
 *  surfaces (create flow + panel) build their field vocabulary from the
 *  selected form via this helper instead of the cross-form union this hook
 *  returns (its draft path reads the FLATTENED legacy content, which merges
 *  every form's fields). */
export function summarizeFormFields(fields: FieldContent[]): FieldDefinitionSummary[] {
  return fields.map((f) => ({
    id: f.fieldDefinitionId,
    name: f.name,
    label: f.label || f.name,
    dataType: f.dataType,
    groupId: f.groupId ?? null,
    groupName: "",
  }));
}

export interface FieldGroupSummary {
  id: string;
  name: string;
}

interface UseJobTypeFieldsReturn {
  fields: FieldDefinitionSummary[];
  groups: FieldGroupSummary[];
  isLoading: boolean;
}

interface ApiJobType {
  id: string;
  name: string;
  fieldGroups: Array<{
    id: string;
    name: string;
    fieldDefinitions: Array<{
      id: string;
      name: string;
      label: string;
      dataType: string;
      groupId: string | null;
    }>;
  }>;
}

/**
 * Reads field definitions for a job type, preferring the FormConfig draft
 * (zustand store) when loaded for this jobTypeId, otherwise falling back
 * to the legacy `GET /api/job-types/:id` endpoint.
 *
 * Fixes #85 — workflow rules / routing / condition editors all consume this
 * hook and previously saw stale legacy data when the operator edited fields
 * in FormConfig.
 *
 * The API call always fires (drives `groups` + serves as fields fallback);
 * the draft, when present, supplies the canonical field list with current
 * dataTypes and any operator-added fields.
 */
export function useJobTypeFields(jobTypeId: string): UseJobTypeFieldsReturn {
  const [apiFields, setApiFields] = useState<FieldDefinitionSummary[]>([]);
  const [apiGroups, setApiGroups] = useState<FieldGroupSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to just the two store slices we care about. zustand's selector
  // form ensures we only re-render when these specific values change.
  const draftJobTypeId = useFormConfigStore((s) => s.version?.jobTypeId);
  const draftFields = useFormConfigStore((s) => s.content?.fields);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api
      .get<ApiJobType>(`/job-types/${jobTypeId}`)
      .then((jt) => {
        if (cancelled) return;
        const grps: FieldGroupSummary[] = jt.fieldGroups.map((g) => ({
          id: g.id,
          name: g.name,
        }));
        const flds: FieldDefinitionSummary[] = [];
        for (const group of jt.fieldGroups) {
          for (const f of group.fieldDefinitions) {
            flds.push({
              id: f.id,
              name: f.name,
              label: f.label,
              dataType: f.dataType,
              groupId: f.groupId,
              groupName: group.name,
            });
          }
        }
        setApiGroups(grps);
        setApiFields(flds);
      })
      .catch(() => {
        if (cancelled) return;
        setApiGroups([]);
        setApiFields([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [jobTypeId]);

  // Draft-first: when the store has a version loaded for THIS jobTypeId
  // and content.fields is defined, return draft-derived fields. Otherwise
  // return whatever the API returned.
  const useDraft = draftJobTypeId === jobTypeId && draftFields !== undefined;

  const fields: FieldDefinitionSummary[] = useDraft
    ? draftFields.map((f) => {
        const groupName = apiGroups.find((g) => g.id === f.groupId)?.name ?? "";
        return {
          id: f.fieldDefinitionId,
          name: f.name,
          label: f.label,
          dataType: f.dataType,
          groupId: f.groupId ?? null,
          groupName,
        };
      })
    : apiFields;

  return { fields, groups: apiGroups, isLoading };
}
